import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import GeoJSON from "ol/format/GeoJSON";
import {Select} from "ol/interaction";
import {pointerMove} from "ol/events/condition";
import Style from "ol/style/Style";
import {Fill, RegularShape, Stroke, Text} from "ol/style";

export default class AirprobeAnnotation{
    constructor() {
        this.map = null;
        this.airprobeAnnotationLayer = new VectorLayer({
            id: 'airprobe_annotation',
            zIndex: 9,
            opacity: 100,
            title: "アノテーション",
            visible: false,
            source: new VectorSource(),
            style: (feature, resolution) => {
                const color = this.getColorByType(feature.get("type"), feature.get('airprobe_type'));
                return new Style({
                    image: new RegularShape({
                        points: 3,
                        angle: 0,
                        rotation: 0,
                        radius: resolution < 0.1 ? 0.45 / resolution : 6,
                        fill: new Fill({ color: color }),
                        stroke: new Stroke({ color: "#000", width: 1 }),
                    }),
                });
            },
        });
        this.featureHoverStyle = (feature, resolution) => {
            const color = this.getColorByType(feature.get("type"), feature.get('airprobe_type'));
            const style = new Style({
                image: new RegularShape({
                    points: 3,
                    angle: 0,
                    rotation: 0,
                    radius: resolution < 0.1 ? 0.45 / resolution : 6,
                    fill: new Fill({ color: color }),
                    stroke: new Stroke({ color: "#000", width: 3 }),
                }),
                text: new Text({
                    font: '14px Calibri,sans-serif',
                    offsetY: resolution < 0.1 ? 0.35 / resolution + 15 : 19,
                    fill: new Fill({
                        color: '#fff',
                        width: 10,
                    }),
                    stroke: new Stroke({
                        color: '#000',
                        width: 3,
                    }),
                }),
            });
            let text = null;
            if (feature.get("type")){
                text = feature.get("type");
            }
            else {
                text = feature.get("airprobe_type");
            }
            style.getText().setText(this.getNameByType(text));
            return style;
        };
        this.hover = new Select({
            condition: pointerMove,
            layers: [this.airprobeAnnotationLayer],
            style: this.featureHoverStyle,
        });
        this.errorTypes = [
            { text: "ホットスポット", value: "cell", color: 'rgba(255, 109, 0, 0.7)' },
            { text: "マルチホットスポット", value: "multicell", color: 'rgba(255, 255, 0, 0.7)' },
            { text: "クラスタバイパス", value: "cluster", color: 'rgba(213, 0, 0, 0.7)' },
            { text: "モジュール異常", value: "module", color: 'rgba(150, 0, 61, 0.7)' },
            { text: "ストリング異常", value: "string", color: 'rgba(64, 0, 124, 0.7)' },
            { text: "ジャンクションボックス過熱", value: "junction", color: 'rgba(76, 175, 80, 0.7)' },
            { text: "ＰＩＤ現象の疑い", value: "pid", color: 'rgba(27, 94, 32, 0.7)' },
            { text: "影", value: "shadow", color: 'rgba(45, 121, 179, 0.7)' },
            { text: "新規", value: "new", color: 'rgba(0, 0, 0, 0.7)' },
        ];
        this.source = [];
        this.layerSource = null;
        this.sourceLength = 0;
    }

    init(map, source){
        this.map = map;
        this.setSource(source);
        this.source.airprobe_abnormality.list.forEach((item) => {
            if (!this.errorTypes.some((i) => i.value === item.airprobe_type)){
                this.errorTypes.push({ text: item.airprobe_type, value: item.airprobe_type, color: 'rgba(0, 0, 0, 0.7)' })
            }
        })
    }

    setSource(data){
        this.source = data;
        this.sourceLength = data.airprobe_abnormality.list.length;
        this.map.removeInteraction(this.hover);
        const features = [];
        data.airprobe_abnormality.list.forEach((item) => {
            if (item.visible || !('visible' in item)){
                let t = null;
                if (item.type || item.reason){
                    t = {
                        type: "Feature",
                        id: item.id,
                        properties: {
                            type: item.type,
                            reason: item.reason,
                            airprobe_thermal_image: item.airprobe_thermal_image,
                            airprobe_color_image: item.airprobe_color_image,
                            airprobe_severity: item.airprobe_severity,
                            airprobe_type: item.airprobe_type,
                        },
                        geometry: {
                            type: "Point",
                            coordinates: item.coordinates,
                        },
                    }
                }
                else {
                    t = {
                        type: "Feature",
                        id: item.id,
                        properties: {
                            type: null,
                            reason: null,
                            airprobe_thermal_image: item.airprobe_thermal_image,
                            airprobe_color_image: item.airprobe_color_image,
                            airprobe_severity: item.airprobe_severity,
                            airprobe_type: item.airprobe_type,
                        },
                        geometry: {
                            type: "Point",
                            coordinates: item.coordinates,
                        },
                    }
                }
                features.push(t)
            }
        });
        const rawSource = {
            type: "FeatureCollection",
            features: features,
        };
        const source = new VectorSource({
            features: new GeoJSON().readFeatures(rawSource),
        });

        this.layerSource = source;

        // Update source
        this.airprobeAnnotationLayer.setSource(source);
        this.map.addInteraction(this.hover);
    }

    getColorByType(type, airprobe_type) {
        const error = this.errorTypes.find(x => x.value === type);
        if (error){
            return this.errorTypes.find(x => x.value === type).color;
        }
        else {
            if (airprobe_type) return this.errorTypes.find(x => x.value === airprobe_type).color;
            else return 'rgba(0, 0, 0, 0.7)';
        }
    }

    getNameByType(type) {
        return this.errorTypes.find(x => x.value === type).text;
    }

    setMap(map){
        this.map = map;
    }

    updateRotate(src, rotate, type, cropImages, annotations){
        const feature = this.airprobeAnnotationLayer.getSource().getFeatures().find(feature => src.includes(feature.get(`airprobe_${type}_image`).name));
        if (feature){
            const tmp = feature.get(`airprobe_${type}_image`);
            tmp.rotate = rotate;
            cropImages.forEach(item => {
                tmp.crop[item.custom_id] = {
                    rect: {width: item.width, height: item.height, top: item.top, left: item.left},
                    errorNote: item.errorNote,
                    errorType: item.errorType,
                    errorReason: item.errorReason
                };
            })
            annotations.forEach(item => {
                tmp.annotation_mark[item.custom_id] = item;
            })
            feature.set(`airprobe_${type}_image`, tmp);
        }
    }

    updateCropImage(src, cropImage, type){
        const feature = this.airprobeAnnotationLayer.getSource().getFeatures().find(feature => src.includes(feature.get(`airprobe_${type}_image`).name));
        if (feature){
            const tmp = feature.get(`airprobe_${type}_image`);
            const crop = tmp['crop'];
            crop[cropImage.custom_id] = {rect: cropImage, errorNote: cropImage.errorNote, errorType: cropImage.errorType, errorReason: cropImage.errorReason};
            tmp['crop'] = crop;
            feature.set(`airprobe_${type}_image`, tmp);
        }
    }

    updateCropAbnormality(src, cropAbnormality, type){
        const feature = this.airprobeAnnotationLayer.getSource().getFeatures().find(feature => src.includes(feature.get(`airprobe_${type}_image`).name));
        if (feature){
            const tmp = feature.get(`airprobe_${type}_image`);
            const annotation_mark = tmp['annotation_mark'];
            annotation_mark[cropAbnormality.custom_id] = cropAbnormality;
            tmp['annotation_mark'] = annotation_mark;
            feature.set(`airprobe_${type}_image`, tmp);
        }
    }

    deleteCropImage(src, cropImage, type){
        const feature = this.airprobeAnnotationLayer.getSource().getFeatures().find(feature => src.includes(feature.get(`airprobe_${type}_image`).name));
        if (feature){
            const tmp = feature.get(`airprobe_${type}_image`);
            let crop = tmp['crop'];
            delete crop[cropImage.custom_id];
            if (!Object.keys(crop).length){
                crop = [];
            }
            tmp['crop'] = crop;
            feature.set(`airprobe_${type}_image`, tmp);
        }
    }

    deleteCropAbnormality(src, cropAbnormality, type){
        const feature = this.airprobeAnnotationLayer.getSource().getFeatures().find(feature => src.includes(feature.get(`airprobe_${type}_image`).name));
        if (feature){
            const tmp = feature.get(`airprobe_${type}_image`);
            let annotation_mark = tmp['annotation_mark'];
            delete annotation_mark[cropAbnormality.custom_id];
            if (!Object.keys(annotation_mark).length){
                annotation_mark = [];
            }
            tmp['annotation_mark'] = annotation_mark;
            feature.set(`airprobe_${type}_image`, tmp);
        }
    }

    filter(airprobeErrorTypes, airprobeSeverities, panelErrorTypes, panelErrorReasons){
        let features = this.layerSource.getFeatures();
        features = features.filter(function (feature) {
            let check = true;
            if (feature.get('airprobe_type')){
                check = check && airprobeErrorTypes.findIndex(error => error === feature.get('airprobe_type')) >= 0;
            }
            if (feature.get('airprobe_severity')){
                check = check && airprobeSeverities.findIndex(error => error === feature.get('airprobe_severity')) >= 0;
            }
            if (feature.get('type')){
                check = check && panelErrorTypes.findIndex(error => error === feature.get('type')) >= 0;
            }
            else {
                check = check && panelErrorTypes.findIndex(error => error === 'undefined') >= 0;
            }
            if (feature.get('reason')){
                check = check && panelErrorReasons.findIndex(error => error === feature.get('reason')) >= 0;
            }
            else {
                check = check && panelErrorReasons.findIndex(error => error === 'undefined') >= 0;
            }
            return check;
        });
        this.airprobeAnnotationLayer.setSource(new VectorSource({
            features: features
        }));
    }
}
